/**
 * Shared EHI / EDX Constants for Frontend
 * Single source of truth — all components import from here.
 * Short band labels align with WEB_APP_EDX_SCORING_HANDOFF (EcoIntel / Marcus pipeline).
 */

// EDX band colours (1-9) — object keyed by band number
// EDX 9 colour aligned with Marcus's render_edx_maps (post-v7.98.0) so the
// raster overlay, parcel chip, legend and report figure are visually identical.
export const EDX_COLORS_OBJ = {
  1: '#6B1E1E', 2: '#B24D22', 3: '#D9A72A', 4: '#fed976',
  5: '#C9C943', 6: '#7BAF44', 7: '#3E9345', 8: '#1F6E3D', 9: '#0B4D26'
};

// Array form (index 0 = band 1) for components using array lookup
export const EDX_COLORS = ['#6B1E1E','#B24D22','#D9A72A','#fed976','#C9C943','#7BAF44','#3E9345','#1F6E3D','#0B4D26'];

export const EDX_TEXT_COLORS = {
  1: '#ffffff', 2: '#ffffff', 3: '#16201C', 4: '#16201C', 5: '#16201C',
  6: '#16201C', 7: '#16201C', 8: '#ffffff', 9: '#ffffff'
};

// Labels — object keyed by band number
export const EDX_LABELS_OBJ = {
  1: 'Collapsed', 2: 'Depleted', 3: 'Degraded', 4: 'Stressed',
  5: 'Transitional', 6: 'Recovering', 7: 'Healthy', 8: 'Thriving', 9: 'Flourishing'
};

// Array form (index 0 = band 1) for components using array lookup
export const EDX_LABELS = ['Collapsed', 'Depleted', 'Degraded', 'Stressed', 'Transitional', 'Recovering', 'Healthy', 'Thriving', 'Flourishing'];

export const PROCESS_COLORS = {
  energy: '#7cb342',
  water: '#1e88e5',
  mineral: '#8d6e63',
  community: '#ab47bc'
};

export const PROCESS_LABELS = {
  energy: {
    full: 'Energy Flow', abbrev: 'EFI', friendly: 'Energy Flow',
    plain: 'Energy Flow', financial: 'Biological yield capacity'
  },
  water: {
    full: 'Water Cycling', abbrev: 'WCI', friendly: 'Water Cycle',
    plain: 'Water Cycle', financial: 'Hydrological resilience'
  },
  mineral: {
    full: 'Mineral Cycling', abbrev: 'MCI', friendly: 'Mineral Cycle',
    plain: 'Mineral Cycle', financial: 'Soil capital maintenance'
  },
  community: {
    full: 'Ecological Community Dynamics', abbrev: 'CDI', friendly: 'Ecological Community Dynamics',
    plain: 'Ecological Community Dynamics', financial: 'Ecological resilience'
  }
};

export const EHI_CONFIG = {
  color: '#333333',
  label: 'Land Health Score',
  fullLabel: 'Land Health Score'
};

// Measurability tiers from EcoIntel Scientific Foundation (EHI Research Paper correlations)
// Tier 1: Directly measurable — dominated by bare ground + live canopy, both high-confidence satellite
// Tier 2: Partially measurable — driven by species richness; phenological diversity is best proxy
// Tier 3: Largely inferred — microfauna, litter incorporation, dung decomposition invisible from space
export const PROCESS_CONFIDENCE = {
  energy: {
    tier: 1, tierLabel: 'Directly measurable', r2: 0.704,
    satelliteStrength: 'Complete spatial coverage at 10m with weekly temporal resolution. NDVI, fAPAR, LAI, and growing season dynamics are directly observable.',
    satelliteLimitation: 'Cannot distinguish canopy quality (leaf area density, photosynthetic efficiency) from quantity. Cloud gaps in optical data.',
    indicators: [1, 3, 4, 5, 6, 12]
  },
  water: {
    tier: 1, tierLabel: 'Directly measurable', r2: 0.627,
    caveat: 'Optical limitation — ensemble-calibrated',
    satelliteStrength: 'BSI and bare ground mapping are the strongest satellite indicators. S1 radar detects soil moisture response after rainfall. Spatial pattern analysis of connected bare ground is uniquely satellite-enabled.',
    satelliteLimitation: 'Cannot directly measure infiltration rate or soil structure. Capping detection is indirect. Spectral confusion between litter and bare soil.',
    indicators: [9, 12, 13, 14, 15]
  },
  mineral: {
    tier: 3, tierLabel: 'Largely inferred', r2: 0.697,
    caveat: 'Inferred from correlated surface signals — direct measurement requires field assessment',
    satelliteStrength: 'Vegetation vigour as nutrient-availability proxy. Litter indices and productivity trends provide indirect evidence.',
    satelliteLimitation: 'Microfauna (#2), litter incorporation (#10), and dung decomposition (#11) are invisible from space. Score achieved through correlated surface signals, not direct measurement.',
    indicators: [2, 9, 10, 11, 12]
  },
  community: {
    tier: 2, tierLabel: 'Partially measurable', r2: 0.674,
    satelliteStrength: 'Phenological diversity and temporal signature complexity capture functional-type diversity. S1 radar texture provides structural information. PALSAR-2 detects woody biomass trends.',
    satelliteLimitation: 'Cannot identify species or assess individual plant condition. Rare species (#7) and undesirable species (#8) invisible at 10m. The "green desert" problem — a monoculture may have identical NDVI to a diverse meadow.',
    indicators: [3, 4, 5, 6, 7, 8]
  }
};

export const INDICATOR_SATELLITE_CONFIDENCE = {
  1:  'high',     // Live Canopy Abundance
  2:  'none',     // Living Organisms (Microfauna)
  3:  'low',      // Warm Season Grasses (via phenology)
  4:  'low',      // Cool Season Grasses (via phenology)
  5:  'low',      // Forbs & Legumes
  6:  'moderate', // Trees & Shrubs (PALSAR-2 radar)
  7:  'none',     // Desirable Rare Species
  8:  'none',     // Undesirable Species
  9:  'moderate', // Litter Abundance (CAI, dry matter indices)
  10: 'none',     // Litter Incorporation
  11: 'none',     // Dung Decomposition
  12: 'high',     // Bare Ground
  13: 'moderate', // Capping (indirect via radar/thermal)
  14: 'low',      // Wind Erosion (change detection)
  15: 'low',      // Water Erosion (topographic analysis)
};

export const HONEST_LIMITATIONS = {
  summary: 'Of 15 EHI indicators, satellite has high confidence on 2 (#1 Live Canopy, #12 Bare Ground), moderate on 3, low on 4, and no direct capability on 6. EFI and WCI are directly measurable. CDI is partially measurable through phenological diversity. MCI is largely inferred from correlated surface signals.',
  fieldAssessmentNeeded: 'Species identification, soil biology, litter quality, forage quality, and tree regeneration require field assessment. Satellite monitoring fills the spatial, temporal, and climate context gaps that field assessment cannot address.',
  combinedStrength: 'Neither approach is sufficient alone. Satellite provides spatial completeness, temporal continuity, and climate context. Field assessment provides species-level, soil-surface, and biological indicators. The combination is more powerful than either alone.'
};

export const BRITTLENESS_DEFAULT = 4;
// Brittleness continuum (handoff 09 §A6): use the Non-brittle / Semi-brittle /
// Brittle terms (Marcus), never low/moderate/high. Copy is the signed-off,
// em-dash-free wording from prototype/analytics.html (the RB-UX reference). The
// numeric range shown beside each label is derived from `range` by consumers,
// so the label stays the bare continuum term (no doubled range).
export const BRITTLENESS_CONTEXT = {
  low:  { range: [1, 3],  label: 'Non-brittle',  description: 'Humid environment. Biological decay is reliable, so dead material breaks down rapidly. Rest promotes recovery.' },
  moderate: { range: [4, 6],  label: 'Semi-brittle', description: 'Seasonal moisture. Decay is intermittent; extended rest can let litter oxidise rather than decompose. Periodic disturbance helps cycle nutrients.' },
  high: { range: [7, 10], label: 'Brittle',      description: 'Arid or erratic moisture. Decay is unreliable and dead material accumulates. Planned disturbance, not rest alone, cycles nutrients.' }
};

export function getBrittlenessCategory(value) {
  if (value <= 3) return BRITTLENESS_CONTEXT.low;
  if (value <= 6) return BRITTLENESS_CONTEXT.moderate;
  return BRITTLENESS_CONTEXT.high;
}

export const TRAJECTORY_COLORS = {
  REGENERATING: '#3E9345',
  STEADY: '#D9A72A',
  DEGRADING: '#B24D22'
};

// Bare ground alert thresholds
export const BARE_GROUND_THRESHOLD = 0.3;
export const BARE_GROUND_WEEKS_ALERT = 4;

export function computeBareGroundAlert(weeks) {
  if (!weeks?.length) return null;
  let maxConsecutive = 0, current = 0, latestBs = null;
  for (const wk of weeks) {
    const bs = wk.raw?.bs;
    if (bs != null) latestBs = bs;
    if (bs != null && bs > BARE_GROUND_THRESHOLD) { current++; maxConsecutive = Math.max(maxConsecutive, current); }
    else { current = 0; }
  }
  return {
    maxConsecutiveWeeks: maxConsecutive,
    isAlert: maxConsecutive >= BARE_GROUND_WEEKS_ALERT,
    latestBs,
    severity: maxConsecutive >= 8 ? 'high' : maxConsecutive >= BARE_GROUND_WEEKS_ALERT ? 'moderate' : 'low'
  };
}

export function computeForageEstimate(weeks) {
  if (!weeks?.length) return null;
  const recent = weeks.slice(-4);
  const ndviVals = recent.map(w => w.raw?.ndvi).filter(v => v != null);
  if (ndviVals.length === 0) return null;
  const avgNdvi = ndviVals.reduce((a, b) => a + b, 0) / ndviVals.length;
  let band, label, color;
  if (avgNdvi >= 0.6) { band = 'high'; label = 'High'; color = '#3E9345'; }
  else if (avgNdvi >= 0.4) { band = 'good'; label = 'Good'; color = '#7BAF44'; }
  else if (avgNdvi >= 0.25) { band = 'moderate'; label = 'Moderate'; color = '#D9A72A'; }
  else { band = 'low'; label = 'Low'; color = '#B24D22'; }
  return { avgNdvi, band, label, color };
}

/**
 * Returns the appropriate process label for the given complexity level.
 * essential → plain, standard → friendly, technical → full (abbrev)
 */
export function getProcessLabel(proc, complexityLevel = 'standard') {
  const p = PROCESS_LABELS[proc];
  if (!p) return proc;
  if (complexityLevel === 'essential') return p.plain;
  if (complexityLevel === 'technical') return `${p.full} (${p.abbrev})`;
  return p.friendly;
}

export function getEHILabel(complexityLevel = 'standard') {
  return 'Land Health Score';
}

// ---------------------------------------------------------------------------
// Land Health Score banding (handoffs/00-foundation.md B1)
// ---------------------------------------------------------------------------
// The Land Health Score is cited as a 1-to-9 level + band name, NEVER as a %
// (% is reserved for methodology tables). These mirror the canonical pipeline
// mapping in backend/analytics-server/edxDescriptions.js (`ehiToEdx` /
// `ehiPctToEdx`): a displayed EHI% maps onto the raw EHI scale (-140..120)
// before banding, so the app reflects Marcus's classification rather than a
// local linear approximation. ALWAYS prefer a pipeline-provided `edx` +
// `edx_label` when present; only derive from a 0-100 score as a fallback.
//
// NOTE (decision-needed, flagged to EcoIntel): the four process sub-scores
// (Energy/Water/Mineral/Community) have no separate pipeline banding. Per the
// handoff's own example ("Water Cycle, level 7, Healthy") they are banded with
// this same mapping; confirm with Marcus.
export function ehiToEdxBand(rawEhiScore) {
  if (rawEhiScore === null || rawEhiScore === undefined || isNaN(Number(rawEhiScore))) return null;
  const score = Number(rawEhiScore);
  if (score >= 100) return 9;
  if (score >= 60) return 8;
  if (score >= 40) return 7;
  if (score >= 20) return 6;
  if (score >= 0) return 5;
  if (score >= -20) return 4;
  if (score >= -40) return 3;
  if (score >= -60) return 2;
  return 1;
}

export function ehiPctToEdxBand(ehiPct) {
  return bandOfEhiPct(ehiPct);
}

// ---------------------------------------------------------------------------
// Decimal EDX (handoffs/06-decimal-edx.md) — mirror EcoIntelReportBuilder
// SiteConfigBuilder/src/ui/dashboard.js + generate_report.py edx_decimal().
// ---------------------------------------------------------------------------

/** Canonical EHI% band floors (grassland headline scale). */
export const EDX_BOUNDS = {
  9: [92.31, 100], 8: [76.92, 92.31], 7: [69.23, 76.92], 6: [61.54, 69.23],
  5: [53.85, 61.54], 4: [46.15, 53.85], 3: [38.46, 46.15], 2: [30.77, 38.46], 1: [0, 30.77],
};

/** Forest-track headline EHI% band floors (forest parcels / forest reference state). */
export const FOREST_EDX_BAND_BOUNDS = {
  9: [85, 100], 8: [75, 85], 7: [65, 75], 6: [60, 65],
  5: [55, 60], 4: [50, 55], 3: [40, 50], 2: [30, 40], 1: [0, 30],
};

/** Per-process band floors (9→2 lower bounds; below last = band 1). Sync with generate_report.PROCESS_BANDS. */
export const PROCESS_BANDS = {
  WCI: [95, 90, 85, 80, 70, 60, 50, 40],
  EFI: [95, 85, 75, 60, 40, 30, 20, 10],
  MCI: [90, 80, 70, 60, 50, 40, 30, 20],
  CDI: [85, 75, 65, 55, 45, 35, 25, 15],
};

export const PROCESS_KEY_TO_ABBREV = {
  energy: 'EFI',
  water: 'WCI',
  mineral: 'MCI',
  community: 'CDI',
};

export function bandOfEhiPct(ehiPct) {
  if (ehiPct === null || ehiPct === undefined || isNaN(Number(ehiPct))) return null;
  const p = Number(ehiPct);
  if (p >= 92.31) return 9;
  if (p >= 76.92) return 8;
  if (p >= 69.23) return 7;
  if (p >= 61.54) return 6;
  if (p >= 53.85) return 5;
  if (p >= 46.15) return 4;
  if (p >= 38.46) return 3;
  if (p >= 30.77) return 2;
  return 1;
}

export function isForestLandState(typeOrState) {
  if (typeOrState == null || typeOrState === '') return false;
  const s = String(typeOrState).trim().toLowerCase();
  if (s === 'forest' || s === 'evergreen_forest' || s === 'deciduous_forest'
    || s === 'mixed_forest' || s === 'woodland' || s === 'wooded_pasture') return true;
  return s.includes('forest') || s.includes('woodland');
}

export function landStateToEdxRegime(typeOrState) {
  return isForestLandState(typeOrState) ? 'forest' : 'grassland';
}

/** Decimal headline EDX string (e.g. "5.4"), floored never rounded. */
export function edxDecimal(ehiPct, bandOverride = null, regime = 'grassland') {
  if (ehiPct === null || ehiPct === undefined || isNaN(Number(ehiPct))) return null;
  const pct = Number(ehiPct);
  const bounds = regime === 'forest' ? FOREST_EDX_BAND_BOUNDS : EDX_BOUNDS;
  let band = bandOverride != null && !isNaN(Number(bandOverride)) ? Math.round(Number(bandOverride)) : null;
  if (band == null) {
    if (regime === 'forest') {
      for (let b = 9; b >= 1; b -= 1) {
        if (pct >= bounds[b][0]) { band = b; break; }
      }
    } else {
      band = bandOfEhiPct(pct);
    }
  }
  if (band == null || band < 1 || band > 9) return null;
  const [lo, hi] = bounds[band];
  let f = hi > lo ? (pct - lo) / (hi - lo) : 0;
  f = Math.min(0.999, Math.max(0, f));
  return (band + Math.floor(f * 10) / 10).toFixed(1);
}

export function edxDecimalNum(ehiPct, bandOverride = null, regime = 'grassland') {
  const s = edxDecimal(ehiPct, bandOverride, regime);
  return s != null ? Number(s) : null;
}

export function processAbbrev(processKey) {
  const k = String(processKey || '').toLowerCase();
  if (PROCESS_KEY_TO_ABBREV[k]) return PROCESS_KEY_TO_ABBREV[k];
  const u = k.toUpperCase();
  if (PROCESS_BANDS[u]) return u;
  return 'WCI';
}

/** Integer process band (1–9) from a 0–100 process score. */
export function procBand(score, processKey) {
  if (score == null || isNaN(Number(score))) return 1;
  const s = Number(score);
  const thresholds = PROCESS_BANDS[processAbbrev(processKey)] || PROCESS_BANDS.WCI;
  for (let i = 0; i < thresholds.length; i += 1) {
    if (s >= thresholds[i]) return 9 - i;
  }
  return 1;
}

/** Decimal process band string (e.g. "6.3"), floored never rounded. */
export function processDecimal(score, processKey) {
  if (score == null || isNaN(Number(score))) return null;
  const s = Number(score);
  const thresholds = PROCESS_BANDS[processAbbrev(processKey)] || PROCESS_BANDS.WCI;
  const band = procBand(s, processKey);
  let lo;
  let hi;
  if (band === 9) {
    lo = thresholds[0];
    hi = 100;
  } else if (band === 1) {
    lo = 0;
    hi = thresholds[7];
  } else {
    const idx = 9 - band;
    lo = thresholds[idx];
    hi = thresholds[idx - 1];
  }
  let f = hi > lo ? (s - lo) / (hi - lo) : 0;
  f = Math.min(0.999, Math.max(0, f));
  return (band + Math.floor(f * 10) / 10).toFixed(1);
}

export function formatEdxChip(ehiPct, { bandOverride = null, regime = 'grassland', withPct = false } = {}) {
  const band = bandOverride != null ? bandOverride : bandOfEhiPct(ehiPct);
  const dec = edxDecimal(ehiPct, band, regime);
  if (dec == null) return null;
  if (withPct && ehiPct != null && !isNaN(Number(ehiPct))) {
    return `EDX ${dec} · ${Number(ehiPct).toFixed(1)}%`;
  }
  return `EDX ${dec}`;
}

export function formatLandHealthLevel(ehiPct, { bandOverride = null, regime = 'grassland' } = {}) {
  const band = bandOverride != null ? bandOverride : bandOfEhiPct(ehiPct);
  const dec = edxDecimal(ehiPct, band, regime);
  const label = edxBandLabel(band);
  if (dec == null) return null;
  return label ? `Land Health ${dec}, ${label}` : `Land Health ${dec}`;
}

export function formatProcessLevel(score, processKey) {
  const band = procBand(score, processKey);
  const dec = processDecimal(score, processKey);
  const label = edxBandLabel(band);
  if (dec == null) return null;
  return label ? `L${dec} · ${label}` : `L${dec}`;
}

// 1..9 band number → human band name (Collapsed..Flourishing). Returns null
// for out-of-range / missing input so callers can fall back gracefully.
export function edxBandLabel(band) {
  if (band === null || band === undefined) return null;
  return EDX_LABELS_OBJ[band] || null;
}

// Convenience for score readouts that previously showed `N%`: returns a
// compact "level N, Band" string for a 0-100 Land-Health/process score (or a
// pipeline band passed directly via `bandOverride`). Returns null when neither
// a band nor a usable score is available so the caller can render a dash.
export function formatScoreAsBand(scorePct, { bandOverride = null, prefix = 'Level', regime = 'grassland' } = {}) {
  const band = bandOverride != null ? bandOverride : bandOfEhiPct(scorePct);
  if (band == null) return null;
  const dec = edxDecimal(scorePct, band, regime);
  const label = edxBandLabel(band);
  return label ? `${prefix} ${dec}, ${label}` : `${prefix} ${dec}`;
}

// Band meaning for Assessments Land Health gauge (handoff 28) and the map legend.
// `good` / `work` / `improvementPathBullets` match RB EDX_BAND_MEANING / prototype
// assessments.html LHBAND — typical-of-band copy, not the site's own readings.
export const EDX_BAND_DETAILS = {
  9: {
    ehiRange: '100 to 120',
    targets: { wci: '>95%', efi: '>95%', mci: '>90%', cdi: '>85%' },
    ecology: ['Water infiltration excellent', 'Zero erosion signals', 'Peak photosynthetic capacity'],
    impacts: ['Maximum profitability', 'Peak carbon sequestration', 'Exceptional ecological community'],
    good: ['Water infiltrates fully, runoff minimal', 'Soils stable, erosion negligible', 'Canopy vigorous, energy capture at its peak', 'Diverse community, rare species thriving'],
    work: ['Hold the line with adaptive management', 'Watch for early-warning shifts', 'Share what works regionally'],
    improvementPathBullets: ['Maintain adaptive management', 'Protect rare species habitat', 'Monitor for early warnings', 'Share knowledge regionally'],
    description: 'Ecosystems not only resilient but actively improved by disturbance, with highly efficient water infiltration, negligible erosion, vigorous canopy with strong solar energy capture, and diverse communities where rare species thrive. The Water Cycle works effectively with minimal runoff, soils remain stable under stress, and functional groups are vigorous across the full diversity spectrum. This represents the pinnacle of ecological health where the system builds capacity through interaction with its environment.'
  },
  8: {
    ehiRange: '60 to 100',
    targets: { wci: '90-95%', efi: '85-95%', mci: '80-90%', cdi: '75-85%' },
    ecology: ['Fast disturbance recovery', 'Good infiltration', 'Minimal erosion'],
    impacts: ['High reliable profits', 'Strong carbon gains', 'High ecological community'],
    good: ['Recovers fast from disturbance', 'Good infiltration, rare erosion', 'High biomass, strong energy capture', 'Well-structured, diverse community'],
    work: ['Close the few remaining stress points', 'Fine-tune disturbance timing', 'Deepen soil reserves'],
    improvementPathBullets: ['Enhance rare species habitat', 'Eliminate stress points', 'Fine-tune disturbance timing', 'Increase functional diversity', 'Build deeper soil reserves'],
    description: 'Ecosystem processes are efficient and mutually reinforcing, creating robust ecological health that recovers quickly from disturbance. Good water infiltration with rare erosion events, stable soils showing no active degradation, high photosynthetic biomass production, and well-structured communities with low undesirable species presence. Systems at this level maintain function through normal climate variability and disturbance events.'
  },
  7: {
    ehiRange: '40 to 60',
    targets: { wci: '85-90%', efi: '75-85%', mci: '70-80%', cdi: '65-75%' },
    ecology: ['Good core functions', 'Mostly stable soils', 'Strong canopy'],
    impacts: ['Good sustainable profits', 'Positive carbon trend', 'Healthy ecological community'],
    good: ['Core cycles functioning well', 'Soils mostly stable', 'Strong canopy growth', 'Healthy community, rare species appear'],
    work: ['Even out seasonal stress dips', 'Strengthen infiltration in limited areas', 'Lift functional diversity in weaker zones'],
    improvementPathBullets: ['Better recovery management', 'Increase litter uniformity', 'Strengthen infiltration', 'Boost functional diversity', 'Refine disturbance timing'],
    description: 'Good infiltration, stable soils, strong canopy growth, and healthy Community Dynamics characterise this band, representing solid ecosystem function suitable for sustained productivity. The Water Cycle is mostly effective with occasional limitations, soils are predominantly stable, photosynthesis is robust with only short stress-related dips, and functional groups are healthy with rare species appearing occasionally. This is the threshold where regenerative management typically aims to maintain or exceed.'
  },
  6: {
    ehiRange: '20 to 40',
    targets: { wci: '80-85%', efi: '60-75%', mci: '60-70%', cdi: '55-65%' },
    ecology: ['Improving but patchy', 'Some runoff in storms', 'Minor soil instability'],
    impacts: ['Variable profitability', 'Carbon trending positive', 'Ecological community rebuilding'],
    good: ['Core cycles working', 'Infiltration good across most fields', 'Canopy rebuilding', 'Community re-establishing'],
    work: ['Reduce runoff in heavy storms', 'Steady the patches of soil instability', 'Support the weaker functional groups'],
    improvementPathBullets: ['Reduce bare ground', 'Extend recovery periods', 'Target weak functional groups', 'Improve water distribution', 'Build soil organic matter'],
    description: 'Ecosystem cycles are mostly working but with limited resilience and patchy diversity: functional, but not yet fully so. Some runoff events occur during heavy rainfall, minor soil instability is beginning to show, canopy exhibits evident seasonal stress dips, and some functional groups show weakness or patchiness. Land in this band needs continued management attention to hold and build on this condition.'
  },
  5: {
    ehiRange: '0 to 20',
    targets: { wci: '70-80%', efi: '40-60%', mci: '50-60%', cdi: '45-55%' },
    ecology: ['Uneven function', 'Frequent runoff', 'Localized erosion'],
    impacts: ['Inconsistent returns', 'Carbon near neutral', 'Declining ecological community'],
    good: ['Water Cycle holding across most fields', 'Ground cover present site-wide', 'Biological activity underway', 'The easy gains are still on the table'],
    work: ['Runoff where cover is thin in storms', 'Erosion or capping on bare patches', 'Canopy uneven, vigour variable', 'Undesirable species creeping in'],
    improvementPathBullets: ['Keep the weaker processes from slipping', 'Reduce soil disturbance', 'Increase ground cover', 'Address any erosion hotspots', 'Lengthen recovery periods'],
    description: 'Some ecosystem processes are holding while others are leaking, creating uneven canopy cover and diversity across the landscape. Runoff is frequent during storms, localised erosion and soil capping appear in vulnerable areas, canopy is uneven with variable vigour, and undesirable species are beginning to increase. This band represents a critical threshold where management decisions will determine whether the system improves towards Level 6 or degrades towards Level 4.'
  },
  4: {
    ehiRange: '-20 to 0',
    targets: { wci: '60-70%', efi: '30-40%', mci: '40-50%', cdi: '35-45%' },
    ecology: ['Patchy vegetation', 'Poor infiltration', 'Pedestalling visible'],
    impacts: ['Low profitability', 'Net carbon loss', 'Compromised ecological community'],
    good: ['Vegetation still present across much of the site', 'Some cycles continue to function', 'Recovery is achievable with intervention'],
    work: ['Poor infiltration, frequent sheet flow', 'Pedestals forming, soil actively lost', 'Canopy thin, litter patchy', 'Key functional groups weakening'],
    improvementPathBullets: ['Major redesign required', 'Reduce disturbance drastically', 'Emergency ground cover', 'Fix all erosion', 'Plan 3-5 year recovery'],
    description: 'Patchy vegetation cover, stuttering ecosystem cycles, and weakening functional group integrity characterize this vulnerable state. Water infiltration is poor with frequent sheet flow across the surface, pedestals begin to appear around plant bases indicating active soil loss, canopy is thin with patchy litter distribution, and key functional groups are noticeably weakening. Systems in this band are highly vulnerable to further degradation without significant management intervention.'
  },
  3: {
    ehiRange: '-40 to -20',
    targets: { wci: '50-60%', efi: '<30%', mci: '30-40%', cdi: '25-35%' },
    ecology: ['Poor infiltration', 'Active rill erosion', 'Low biomass'],
    impacts: ['Marginal returns', 'Major carbon loss', 'Depleted ecological community'],
    good: ['Pioneer species still establishing', 'A clear restoration pathway exists'],
    work: ['Poor infiltration, regular rill erosion', 'Low biomass, slow recovery', 'Undesirable species dominant', 'Rare species largely absent'],
    improvementPathBullets: ['Stop extractive use now', 'Mechanical erosion control', 'Pioneer species urgently', 'Consider topsoil import', 'Plan 5-10 year restoration'],
    description: 'Poor water infiltration, visible erosion signals, low photosynthetic capacity, and rising dominance of undesirable species indicate significant ecosystem dysfunction. Regular runoff and rill formation occur, erosion indicators are common across the landscape, biomass production is low with slow recovery from disturbance, and undesirable species are becoming common while rare species are rarely seen. Land in this band requires comprehensive management redesign to prevent further decline.'
  },
  2: {
    ehiRange: '-60 to -40',
    targets: { wci: '40-50%', efi: '<20%', mci: '20-30%', cdi: '15-25%' },
    ecology: ['Severe water loss', 'Widespread capping', 'Minimal biomass'],
    impacts: ['Economically unsustainable', 'Major carbon source', 'Ecological community collapse'],
    good: ['Some hardy vegetation persists', 'Intervention can still turn it around'],
    work: ['Most rainfall lost to runoff', 'Active erosion, widespread capping', 'Minimal biomass', 'Community diversity sharply reduced'],
    improvementPathBullets: ['Stop the damage first', 'Bunched stock on dry ground, then long rest', 'Intensive stabilisation', 'Emergency seeding', 'Timeline 10+ years'],
    description: 'Severe leakage of water and minerals, weak canopy, and failing diversity indicate land on the edge of collapse requiring urgent intervention. Little water infiltrates with most rainfall lost to runoff, active erosion with widespread soil surface capping, low photosynthetic activity producing minimal biomass, and functional groups actively failing with sharply reduced diversity. Without immediate management changes, systems in this band are likely to collapse to Level 1.'
  },
  1: {
    ehiRange: '< -60',
    targets: { wci: '<40%', efi: '<10%', mci: '<20%', cdi: '<15%' },
    ecology: ['Immediate water runoff', 'Severe ongoing erosion', 'Canopy absent/dead'],
    impacts: ['Complete economic failure', 'Maximum emissions', 'Functional extinction'],
    good: ['A few stress-tolerant species hold on', 'Every restoration journey starts here'],
    work: ['Water runs off immediately', 'Severe, ongoing erosion', 'Canopy absent or dead', 'Biological community collapsed'],
    improvementPathBullets: ['Engineering solutions essential', 'Import topsoil likely', 'Intensive irrigation needed', 'Timeline 15-20+ years', 'Consider alternative land use'],
    description: 'Water runs off immediately, severe soil erosion is active, canopy is nearly absent or dominated by standing dead material, and ecological communities have collapsed towards a handful of stress-tolerant species. Flashy water loss occurs with every rainfall event, erosion is severe and ongoing, bare ground or dead vegetation dominate the landscape, and biological diversity has effectively collapsed. This represents complete ecosystem dysfunction requiring intensive, long-term restoration intervention.'
  }
};
